"use client";

import { useEffect, useRef, useState, MouseEvent } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import Magnetic from "./ui/Magnetic";

function SpotlightCard({ children, className, span }: { children: React.ReactNode, className?: string, span: string }) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function onMouseMove({ currentTarget, clientX, clientY }: MouseEvent) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <motion.div
      onMouseMove={onMouseMove}
      className={`group relative overflow-hidden rounded-3xl border border-white/5 bg-gradient-to-br from-white/[0.03] to-transparent ${span} ${className}`}
    >
      <motion.div
        className="pointer-events-none absolute -inset-px rounded-3xl opacity-0 transition duration-300 group-hover:opacity-100"
        style={{
          background: useTransform(
            [mouseX, mouseY],
            ([x, y]) => `radial-gradient(600px circle at ${x}px ${y}px, rgba(99, 102, 241, 0.1), transparent 85%)`
          ),
        }}
      />
      {children}
    </motion.div>
  );
}

function CountUp({ target, suffix, trigger }: { target: number; suffix: string; trigger: boolean }) {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!trigger) return;
    let start = 0;
    const end = target;
    const duration = 1800; // 1.8s
    const stepTime = 16;
    const totalSteps = duration / stepTime;
    const increment = end / totalSteps;
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.round(start));
      }
    }, stepTime);
    
    return () => clearInterval(timer);
  }, [trigger, target]);
  
  return <span>{count.toLocaleString()}{suffix}</span>;
}

export default function Stats() {
  const [triggered, setTriggered] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTriggered(true);
        observer.disconnect();
      }
    }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="section-pad relative z-10" aria-labelledby="stats-heading">
      <div className="max-w-7xl mx-auto px-6">
        <h2 id="stats-heading" className="sr-only">
          Career highlights and impact
        </h2>
        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-3 gap-6 auto-rows-[160px]">
          {/* Main Stat: Experience */}
          <SpotlightCard span="md:col-span-2 md:row-span-2 flex flex-col justify-center items-center text-center p-8 bg-[#0a071c]/50 border-indigo-500/10">
            <Magnetic>
              <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-6">
                <i className="fi fi-rr-rocket-lunch text-3xl text-indigo-400"></i>
              </div>
            </Magnetic>
            <h3 className="text-6xl font-black text-white mb-2 font-[var(--font-poppins)] tracking-tight">
              <CountUp target={4} suffix="+" trigger={triggered} />
            </h3>
            <p className="text-gray-400 text-base font-bold uppercase tracking-wider">Years Experience</p>
          </SpotlightCard>

          {/* Projects Completed */}
          <SpotlightCard span="md:col-span-2 md:row-span-1 flex items-center justify-between px-8 py-6">
            <div>
              <h3 className="text-4xl font-black text-purple-400 mb-1 font-[var(--font-poppins)] tracking-tight">
                <CountUp target={25} suffix="+" trigger={triggered} />
              </h3>
              <p className="text-gray-400 text-xs font-bold uppercase tracking-wider">Projects Delivered</p>
            </div>
            <Magnetic>
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <i className="fi fi-rr-briefcase text-2xl text-purple-400"></i>
              </div>
            </Magnetic>
          </SpotlightCard>

          {/* Production Deployments */}
          <SpotlightCard span="md:col-span-1 md:row-span-1 flex flex-col justify-center p-6 bg-black/10">
            <h3 className="text-3xl font-black text-pink-400 font-[var(--font-poppins)]">
              <CountUp target={50} suffix="+" trigger={triggered} />
            </h3>
            <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mt-1.5">Deployments</p>
          </SpotlightCard>

          {/* Lines of Code */}
          <SpotlightCard span="md:col-span-1 md:row-span-1 flex flex-col justify-center p-6 bg-black/10">
            <h3 className="text-3xl font-black text-cyan-400 font-[var(--font-poppins)]">
              <CountUp target={100} suffix="K+" trigger={triggered} />
            </h3>
            <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mt-1.5">Lines of Code</p>
          </SpotlightCard>

          {/* Technologies Mastered */}
          <SpotlightCard span="md:col-span-1 md:row-span-1 flex flex-col justify-center p-6 bg-black/10">
            <h3 className="text-3xl font-black text-emerald-400 font-[var(--font-poppins)]">
              <CountUp target={15} suffix="+" trigger={triggered} />
            </h3>
            <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mt-1.5">Technologies</p>
          </SpotlightCard>

          {/* GitHub Contributions */}
          <SpotlightCard span="md:col-span-1 md:row-span-1 flex flex-col justify-center p-6 bg-black/10">
            <h3 className="text-3xl font-black text-amber-400 font-[var(--font-poppins)]">
              <CountUp target={1000} suffix="+" trigger={triggered} />
            </h3>
            <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mt-1.5">Commits/PRs</p>
          </SpotlightCard>

          {/* Countries Reached */}
          <SpotlightCard span="md:col-span-1 md:row-span-1 flex flex-col justify-center p-6 bg-black/10">
            <h3 className="text-3xl font-black text-red-400 font-[var(--font-poppins)]">
              <CountUp target={5} suffix="+" trigger={triggered} />
            </h3>
            <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mt-1.5">Countries Reached</p>
          </SpotlightCard>

          {/* Users Served */}
          <SpotlightCard span="md:col-span-1 md:row-span-1 flex flex-col justify-center p-6 bg-black/10">
            <h3 className="text-3xl font-black text-blue-400 font-[var(--font-poppins)]">
              <CountUp target={10} suffix="K+" trigger={triggered} />
            </h3>
            <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mt-1.5">Users Served</p>
          </SpotlightCard>
        </div>
      </div>
    </section>
  );
}
